import { fetchUserMiscrosoftImage } from '@/lib';
import type { IManagedUserSummary } from '@/types';
import { useCallback } from 'react';

export interface UseManagedUserImageParams {
  /** The currently active managed user whose image is being fetched. */
  managerActiveUser: IManagedUserSummary | null;
  /** Function to update the active managed user's data in the global store. */
  updateManagerActiveUser: (updates: Partial<IManagedUserSummary>) => void;
}

/**
 * Custom hook to fetch and manage the active managed user's profile image for the manager dashboard.
 *
 * This hook encapsulates the logic for:
 *  - Fetching the managed user's Microsoft profile image from the API using the provided params.
 *  - Updating the global store with the new image URL if it doesn't already exist.
 *  - Handling errors gracefully without disrupting the user experience.
 */
export const useManagedUserImage = ({
  managerActiveUser,
  updateManagerActiveUser,
}: UseManagedUserImageParams) => {
  const fetchUserImage = useCallback(async () => {
    // Guard: no active user means there is no email to resolve image for.
    if (!managerActiveUser) return;

    try {
      // Request image URL using managed user's email.
      const imageUrl = await fetchUserMiscrosoftImage(managerActiveUser.userEmail);

      // Only update when a new URL exists and current store has no image yet.
      // This avoids unnecessary store writes and re-renders.
      if (imageUrl && managerActiveUser && !managerActiveUser.userImage) {
        updateManagerActiveUser({ userImage: imageUrl });
      }
    } catch (error) {
      // Non-blocking failure: dashboard can continue rendering without image.
      console.error('Error fetching user image:', error);
    }
  }, [managerActiveUser, updateManagerActiveUser]);

  // ============================================
  // Expose the fetch function for use in components.
  // This allows components to trigger image fetches as needed, while keeping
  // the logic encapsulated and reusable across different parts of the dashboard.
  // ============================================
  return {
    /**
     * Fetches the managed user's Microsoft profile image using their email. It checks
     * for the presence of an active user and guards against duplicate fetches if an image
     * already exists in the store. If a new image URL is successfully retrieved, it updates
     * the global store with the new image URL. Any errors during the fetch are caught and
     * logged without disrupting the user experience.
     */
    fetchUserImage,
  };
};
